import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus,
  RefreshCcw,
  Search,
  X,
  Loader2,
  Trash2,
  Tags,
  Layers,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MyContext from "../../../MyContext";
import "../../Order/Udhari/CreditStyle.css";

/** Endpoints */
const API = {
  LIST: "/api/Inventory/v1.0/getAllRackDetails",
  ADD: "/api/Inventory/v1.0/insertRackDetails",
  DEL: "/api/Inventory/v1.0/deleteRackDetails",
};

const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";

/** Helpers */
const safeParseJSON = (s) => {
  try {
    const v = JSON.parse(s || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export default function RackManager({ userID }) {
  const { baseUrl } = useContext(MyContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    rackNumber: "",
    category: "",
    tags: "",
  });

  const [deletingId, setDeletingId] = useState(null);

  const call = (path, payload) =>
    axios.post(`${baseUrl}${path}`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${BASIC_AUTH}`,
      },
    });

  /** Load racks (expects pk_rack_id, rack_no, category, tags(json-string), status) */
  async function fetchRacks() {
    if (!userID) return toast.error("Missing userid");
    try {
      setLoading(true);
      const res = await call(API.LIST, { userid: userID });
      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.response) ? res.data.response : [];
        setRows(list);
      } else {
        setRows([]);
        toast.error(res?.data?.responseMessage || "Failed to fetch racks");
      }
    } catch (e) {
      console.error(e);
      toast.error("API error while fetching racks");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, baseUrl]);

  /** Add rack (your insert API spec stays the same) */
  function validate() {
    if (!form.rackNumber.trim()) return "Rack number is required";
    return null;
  }
  async function addRack() {
    const err = validate();
    if (err) return toast.warning(err);

    try {
      setSubmitting(true);
      const res = await call(API.ADD, {
        rackNumber: String(form.rackNumber).trim(),
        category: String(form.category || "").trim(),
        tags: String(form.tags || "").trim(), // backend stores it as JSON string later
      });
      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Rack added");
        setOpen(false);
        setForm({ rackNumber: "", category: "", tags: "" });
        fetchRacks();
      } else {
        toast.error(res?.data?.responseMessage || "Add failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("API error while adding rack");
    } finally {
      setSubmitting(false);
    }
  }

  /** Delete rack (uses pk_rack_id as rackId) */
  async function deleteRack(pk_rack_id) {
    if (!pk_rack_id) return toast.error("Missing rackId");
    if (!window.confirm("Delete this rack? This cannot be undone.")) return;

    try {
      setDeletingId(String(pk_rack_id));
      const res = await call(API.DEL, { rackId: String(pk_rack_id) });
      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Rack deleted");
        fetchRacks();
      } else {
        toast.error(res?.data?.responseMessage || "Delete failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("API error while deleting rack");
    } finally {
      setDeletingId(null);
    }
  }

  /** Search filter across rack_no/category/tags/status */
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const tagsStr = Array.isArray(r.tags) ? r.tags.join(",") : String(r.tags || "");
      return [r.pk_rack_id, r.rack_no, r.category, tagsStr, r.status]
        .map((v) => String(v || "").toLowerCase())
        .some((s) => s.includes(needle));
    });
  }, [rows, q]);

  function openAdd() {
    setForm({ rackNumber: "", category: "", tags: "" });
    setOpen(true);
  }
  function closeDrawer() {
    if (!submitting) setOpen(false);
  }

  return (
    <div className="admin-credit-wrap p-4">
      <ToastContainer position="top-right" theme="colored" closeOnClick pauseOnHover={false} />

      {/* Header */}
      <div className="admin-credit-header">
        <div>
          <h3 className="admin-credit-title">Rack Management</h3>
          <p className="admin-credit-subtitle">
            Create, organize and manage storage racks by category and tags
          </p>
        </div>
        <div className="admin-credit-actions">
          <button className="admin-btn admin-btn-primary btn--md" onClick={openAdd} disabled={submitting || loading}>
            <Plus size={18} /> Add Rack
          </button>
          <button className="admin-btn admin-btn-neutral btn--md" onClick={fetchRacks} disabled={loading}>
            <RefreshCcw size={18} /> Refresh
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="admin-credit-toolbar">
        <div className="admin-input with-icon">
          <Search size={16} className="admin-input-icon" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by rack #, category, tags, status…"
          />
        </div>
        <div className="admin-meta">
          <span className="admin-badge">Total: {rows.length}</span>
          {loading ? <span className="admin-badge muted">Loading…</span> : null}
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>Rack #</th>
              <th>Category</th>
              <th>Tags</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="center">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="center muted">No racks found</td></tr>
            ) : (
              filtered.map((r) => {
                const id = r.pk_rack_id;
                const rackNo = r.rack_no || "—";
                const category = r.category || "—";
                const tagsArr = Array.isArray(r.tags) ? r.tags : safeParseJSON(r.tags);
                const tagsStr = tagsArr.join(", ");
                const isActive = String(r.status || "").toUpperCase() === "Y";

                return (
                  <tr key={id || rackNo}>
                    <td className="mono">{rackNo}</td>
                    <td>
                      <span className="admin-badge">
                        <Layers size={12} style={{ marginRight: 6 }} />
                        {category}
                      </span>
                    </td>
                    <td className="trunc" title={tagsStr}>
                      <span className="admin-badge muted">
                        <Tags size={12} style={{ marginRight: 6 }} />
                        {tagsStr || "—"}
                      </span>
                    </td>
                    <td>
                      {isActive ? (
                        <span className="admin-badge success">
                          <ShieldCheck size={12} style={{ marginRight: 6 }} />
                          Active
                        </span>
                      ) : (
                        <span className="admin-badge danger">
                          <ShieldOff size={12} style={{ marginRight: 6 }} />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="row-actions">
                      <button
                        className="admin-icon-btn danger"
                        title="Delete"
                        onClick={() => deleteRack(id)}
                        disabled={String(deletingId) === String(id)}
                      >
                        {String(deletingId) === String(id) ? (
                          <Loader2 className="admin-spin" size={16} />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ADD RACK DRAWER */}
      {open && (
        <div className="admin-drawer-backdrop" onClick={closeDrawer}>
          <div className="admin-drawer admin-drawer-compact" onClick={(e) => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h4 className="admin-drawer-title">Add Rack</h4>
              <button className="admin-icon-btn" onClick={closeDrawer}>
                <X size={18} />
              </button>
            </div>

            <div className="admin-drawer-body admin-drawer-form admin-drawer-scroll">
              <div className="admin-field">
                <label>
                  Rack Number <span className="req">*</span>
                </label>
                <input
                  className="admin-input-control mono"
                  value={form.rackNumber}
                  onChange={(e) => setForm((f) => ({ ...f, rackNumber: e.target.value }))}
                  placeholder="e.g., 1, A-01, B-12"
                  required
                />
              </div>

              <div className="admin-field">
                <label>Category</label>
                <input
                  className="admin-input-control"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="e.g., grocery"
                />
              </div>

              <div className="admin-field">
                <label>Tags (comma separated)</label>
                <input
                  className="admin-input-control"
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                  placeholder="e.g., dairy, vegetable"
                />
              </div>
            </div>

            <div className="admin-drawer-footer sticky-footer">
              <button type="button" className="admin-btn admin-btn-neutral btn--md" onClick={closeDrawer}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary btn--md"
                onClick={addRack}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="admin-spin" size={16} /> : null}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
